/**
 * One product, two venue types. The backend is venue-neutral and speaks in service locations
 * (`TABLE` or `SEAT`); the venue type (`RESTAURANT` or `CINEMA`) follows from it. Components never branch
 * on the venue: they ask these helpers for the right words.
 */

export const VENUE_TYPES = ['RESTAURANT', 'CINEMA'] as const;
export type VenueType = (typeof VENUE_TYPES)[number];

export const LOCATION_TYPES = ['TABLE', 'SEAT'] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];

export function isLocationType(value: unknown): value is LocationType {
  return typeof value === 'string' && (LOCATION_TYPES as readonly string[]).includes(value);
}

export function venueTypeForLocation(type: LocationType): VenueType {
  return type === 'SEAT' ? 'CINEMA' : 'RESTAURANT';
}

export interface VenueVocabulary {
  /** "Table" or "Seat". */
  locationNoun: string;
  /** Customer-facing wording for a served item. */
  served: string;
  /** Short staff wording for the same step ("Served" at a table, "Delivered" at a seat). */
  servedShort: string;
  /** Whether a guest count means anything here. */
  showsGuestCount: boolean;
}

const VOCABULARY: Record<LocationType, VenueVocabulary> = {
  TABLE: { locationNoun: 'Table', served: 'Served', servedShort: 'Served', showsGuestCount: true },
  SEAT: {
    locationNoun: 'Seat',
    served: 'Delivered to your seat',
    servedShort: 'Delivered',
    showsGuestCount: false,
  },
};

export function venueVocabulary(type: LocationType): VenueVocabulary {
  return VOCABULARY[type];
}

/** "Table 12" or "Seat B12". A label that already starts with the noun is left alone. */
export function formatLocation(type: LocationType, label: string): string {
  const noun = VOCABULARY[type].locationNoun;
  const trimmed = label.trim();
  if (trimmed.toLowerCase().startsWith(noun.toLowerCase())) return trimmed;
  return `${noun} ${trimmed}`;
}

/** "Rahul · 3 guests" in a restaurant, "Rahul" in a cinema. */
export function formatParty(type: LocationType, name: string, guestCount?: number | null): string {
  const showCount =
    VOCABULARY[type].showsGuestCount && typeof guestCount === 'number' && guestCount > 0;
  if (!showCount) return name;
  return `${name} · ${guestCount} ${guestCount === 1 ? 'guest' : 'guests'}`;
}
